import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useActivityContext, ACTIVITY_TYPES } from "../context/ActivityContext";

const TAB_ROUTE_MAP = {
  all: ACTIVITY_TYPES.ALL,
  email: ACTIVITY_TYPES.EMAIL,
  sms: ACTIVITY_TYPES.SMS,
  notes: ACTIVITY_TYPES.NOTES,
};

const DEFAULT_COLLECTION_STATE = {
  items: [],
  meta: {
    total_items: 0,
    total_pages: 1,
    current_page: 1,
    per_page: 20,
    has_next: false,
    has_previous: false,
    next_cursor: null,
  },
  filters: {},
  search: {},
  orderBy: ["created_at"],
  orderDirection: ["DESC"],
  loading: false,
  error: null,
};

const formatDate = (iso) => {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const formatTimestamp = (iso) => {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  const time = date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  const day = date.toLocaleDateString(undefined, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
  return `${time} · ${day}`;
};

export const mapEmailItems = (items = []) =>
  items.map((item, index) => ({
    id: `email-${item.thread ?? index}-${item.created_at ?? index}`,
    title: item.subject || "Untitled email",
    sender: item.sender || "-",
    opens: String(item.opens ?? 0),
    clicks: String(item.clicks ?? 0),
    status: item.state || "UNKNOWN",
    thread: item.thread ?? "-",
    timestamp: formatTimestamp(item.created_at),
    createdAt: item.created_at,
    raw: item,
  }));

export const mapNoteItems = (items = []) =>
  items.map((item, index) => ({
    id: `note-${index}-${item.createdAt ?? item.created_at ?? index}`,
    title: item.noteTitle || item.note_title || "Untitled note",
    description: item.description || "-",
    category: item.category || "GENERAL",
    owner: item.ownerFullName || item.owner_full_name || "-",
    lastUpdated: formatDate(item.lastUpdate || item.last_update),
    creationDate: formatDate(item.createdAt || item.created_at),
    createdAt: item.createdAt || item.created_at,
    note_uuid:
      item.note_uuid ||
      item.uuid ||
      item.id ||
      item.noteId ||
      item.activity_uuid ||
      null,
    raw: item,
  }));

export const mapSmsItems = (items = []) =>
  items.map((item, index) => ({
    id: `sms-${index}-${item.created_at ?? index}`,
    type: item.sms_name || "SMS",
    From: item.from || "-",
    To: item.to || "-",
    Replies: String(item.replies ?? 0),
    Status: item.status || "UNKNOWN",
    Body: item.body || "-",
    timestamp: formatTimestamp(item.created_at),
    createdAt: item.created_at,
    raw: item,
  }));

// Builder for endpoints with pagination (Email/SMS/Notes)
const buildFetchParams = (contactId, collection, overrides = {}) => ({
  party_uuid: contactId,
  page: overrides.page || collection.meta.current_page,
  page_size: overrides.page_size || collection.meta.per_page,
  order_by: overrides.order_by || collection.orderBy,
  order_direction: overrides.order_direction || collection.orderDirection,
  filters: overrides.filters || collection.filters,
  search: overrides.search || collection.search,
});

/** Params for /v1/workspace/communication/list (Recent Activity / All tab) */
export const buildFetchParamsAll = (contactId, overrides = {}) => {
  const {
    filters,
    search,
    activity_type,
    page,
    page_size,
    order_by,
    order_direction,
  } = overrides || {};
  return {
    party_uuid: contactId,
    page: page ?? 1,
    page_size: page_size ?? 10,
    order_by: order_by ?? "last_update",
    order_direction: order_direction ?? "DESC",
    ...(activity_type ? { activity_type } : {}),
    ...(filters ? { filters } : {}),
    ...(search ? { search } : {}),
  };
};

export const buildFetchParamsNotes = (contactId, overrides = {}) => ({
  party_uuid: contactId,
  page: overrides.page ?? 1,
  page_size: overrides.page_size ?? 20,
  order_by: overrides.order_by ?? ["created_at"],
  order_direction: overrides.order_direction ?? ["DESC"],
  ...(overrides.filters ? { filters: overrides.filters } : {}),
  ...(overrides.search ? { search: overrides.search } : {}),
});

export const buildAllMeta = (items) => ({
  total_items: items.length,
  total_pages: 1,
  current_page: 1,
  per_page: items.length || 1,
  has_next: false,
  has_previous: false,
  next_cursor: null,
});

export const useActivityPage = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    activeTab,
    collections,
    setActiveTab,
    fetchActivity,
    clearActivityData,
    createNoteActivity,
    getNoteDetailActivity,
    updateNoteActivity,
    deleteNoteActivity,
    removeNoteOptimistically,
  } = useActivityContext();

  const emailCollection =
    collections[ACTIVITY_TYPES.EMAIL] || DEFAULT_COLLECTION_STATE;
  const notesCollection =
    collections[ACTIVITY_TYPES.NOTES] || DEFAULT_COLLECTION_STATE;
  const smsCollection =
    collections[ACTIVITY_TYPES.SMS] || DEFAULT_COLLECTION_STATE;

  const emailItems = useMemo(
    () => mapEmailItems(emailCollection.items),
    [emailCollection.items]
  );
  const noteItems = useMemo(
    () => mapNoteItems(notesCollection.items),
    [notesCollection.items]
  );
  const smsItems = useMemo(
    () => mapSmsItems(smsCollection.items),
    [smsCollection.items]
  );

  const allItems = useMemo(() => {
    const combined = [
      ...emailItems.map((item) => ({
        type: "Email",
        data: item,
        createdAt: item.createdAt,
      })),
      ...noteItems.map((item) => ({
        type: "Notes",
        data: item,
        createdAt: item.createdAt,
      })),
      ...smsItems.map((item) => ({
        type: "SMS",
        data: item,
        createdAt: item.createdAt,
      })),
    ];
    return combined.sort((a, b) => {
      const aTime = new Date(a.createdAt || 0).getTime();
      const bTime = new Date(b.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }, [emailItems, noteItems, smsItems]);

  const allCollection = useMemo(
    () => ({
      items: allItems,
      meta: buildAllMeta(allItems),
      filters: {},
      search: {},
      orderBy: ["created_at"],
      orderDirection: ["DESC"],
      loading:
        emailCollection.loading ||
        notesCollection.loading ||
        smsCollection.loading,
      error:
        emailCollection.error || notesCollection.error || smsCollection.error,
    }),
    [allItems, emailCollection, notesCollection, smsCollection]
  );

  // Track loaded tabs to prevent duplicate API calls
  const loadedTabsRef = useRef(new Set());
  const lastContactIdRef = useRef(null);

  const syncTabFromRoute = useCallback(() => {
    const path = location.pathname.split("/").pop();
    const mappedTab = TAB_ROUTE_MAP[path] || ACTIVITY_TYPES.ALL;
    if (mappedTab !== activeTab) setActiveTab(mappedTab);
  }, [location.pathname, activeTab, setActiveTab]);

  useEffect(() => {
    syncTabFromRoute();
  }, [syncTabFromRoute]);

  // Reset loaded tabs when contactId changes
  useEffect(() => {
    if (contactId !== lastContactIdRef.current) {
      loadedTabsRef.current.clear();
      lastContactIdRef.current = contactId;
    }
  }, [contactId]);

  // Initial load per tab - OPTIMIZED to prevent duplicate calls
  useEffect(() => {
    if (!contactId || !activeTab) return;

    // Create a unique key for this tab + contactId combination
    const loadKey = `${contactId}-${activeTab}`;

    // Check if we've already loaded this specific combination
    // This prevents duplicate calls when React re-renders or Strict Mode runs
    if (loadedTabsRef.current.has(loadKey)) {
      return;
    }

    // Mark as loading IMMEDIATELY to prevent duplicate calls
    // This must happen before the async call to prevent race conditions
    loadedTabsRef.current.add(loadKey);

    // Determine which collection to use for params
    const activeCollection =
      activeTab === ACTIVITY_TYPES.EMAIL
        ? emailCollection
        : activeTab === ACTIVITY_TYPES.SMS
        ? smsCollection
        : activeTab === ACTIVITY_TYPES.NOTES
        ? notesCollection
        : null; // ALL doesn't need a collection reference

    // Always load when switching tabs - the tracking prevents duplicates
    fetchActivity(
      activeTab,
      activeTab === ACTIVITY_TYPES.ALL
        ? buildFetchParamsAll(contactId)
        : buildFetchParams(contactId, activeCollection)
    ).catch(() => {
      // On error, remove from loaded set to allow retry
      loadedTabsRef.current.delete(loadKey);
    });
    // Note: On success, we keep it in loadedTabsRef to prevent future duplicates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, activeTab, fetchActivity]);

  useEffect(() => () => clearActivityData(), [clearActivityData]);

  const handleTabChange = useCallback(
    (tab) => {
      const normalizedTab =
        ACTIVITY_TYPES[tab.toUpperCase()] || ACTIVITY_TYPES.ALL;
      setActiveTab(normalizedTab);

      const routeEntry = Object.entries(TAB_ROUTE_MAP).find(
        ([, value]) => value === normalizedTab
      );
      if (routeEntry) navigate(routeEntry[0]);
    },
    [navigate, setActiveTab]
  );

  const handleFetch = useCallback(
    (tab, overrideParams = {}, forceRefresh = false) => {
      if (!contactId) return Promise.resolve(null);
      const normalizedTab = ACTIVITY_TYPES[tab.toUpperCase()] || tab;

      // If forcing refresh, remove from loaded set to allow reload
      if (forceRefresh) {
        const loadKey = `${contactId}-${normalizedTab}`;
        loadedTabsRef.current.delete(loadKey);
      }

      const targetCollection =
        normalizedTab === ACTIVITY_TYPES.EMAIL
          ? emailCollection
          : normalizedTab === ACTIVITY_TYPES.SMS
          ? smsCollection
          : normalizedTab === ACTIVITY_TYPES.NOTES
          ? notesCollection
          : allCollection;

      const params =
        normalizedTab === ACTIVITY_TYPES.ALL
          ? buildFetchParamsAll(contactId, overrideParams)
          : buildFetchParams(contactId, targetCollection, overrideParams);

      return fetchActivity(normalizedTab, params);
    },
    [
      contactId,
      fetchActivity,
      emailCollection,
      smsCollection,
      notesCollection,
      allCollection,
    ]
  );

  const handlePageChange = useCallback(
    (nextPage) => {
      handleFetch(activeTab, { page: nextPage });
    },
    [activeTab, handleFetch]
  );

  const handleItemsPerPageChange = useCallback(
    (nextPageSize) => {
      handleFetch(activeTab, { page: 1, page_size: nextPageSize });
    },
    [activeTab, handleFetch]
  );

  const handleRefresh = useCallback(() => {
    // Force refresh by clearing the loaded flag
    handleFetch(activeTab, {}, true);
  }, [activeTab, handleFetch]);

  const currentCollection = useMemo(() => {
    switch (activeTab) {
      case ACTIVITY_TYPES.EMAIL:
        return { ...emailCollection, items: emailItems };
      case ACTIVITY_TYPES.SMS:
        return { ...smsCollection, items: smsItems };
      case ACTIVITY_TYPES.NOTES:
        return { ...notesCollection, items: noteItems };
      case ACTIVITY_TYPES.ALL:
      default:
        return allCollection;
    }
  }, [
    activeTab,
    emailCollection,
    emailItems,
    smsCollection,
    smsItems,
    notesCollection,
    noteItems,
    allCollection,
  ]);

  const buildTabProps = useCallback(
    (tab) => {
      const normalizedTab = ACTIVITY_TYPES[tab.toUpperCase()];

      if (normalizedTab === ACTIVITY_TYPES.ALL) {
        return {
          items: allCollection.items,
          meta: allCollection.meta,
          filters: allCollection.filters,
          search: allCollection.search,
          orderBy: allCollection.orderBy,
          orderDirection: allCollection.orderDirection,
          loading: allCollection.loading,
          error: allCollection.error,
          refresh: () => handleFetch(ACTIVITY_TYPES.ALL, {}, true),
          fetch: (overrideParams) =>
            handleFetch(ACTIVITY_TYPES.ALL, overrideParams, false),
        };
      }

      if (normalizedTab === ACTIVITY_TYPES.EMAIL) {
        return {
          ...emailCollection,
          items: emailItems,
          refresh: () => handleFetch(ACTIVITY_TYPES.EMAIL, {}, true),
          fetch: (overrideParams) =>
            handleFetch(ACTIVITY_TYPES.EMAIL, overrideParams, false),
        };
      }

      if (normalizedTab === ACTIVITY_TYPES.SMS) {
        return {
          ...smsCollection,
          items: smsItems,
          refresh: () => handleFetch(ACTIVITY_TYPES.SMS, {}, true),
          fetch: (overrideParams) =>
            handleFetch(ACTIVITY_TYPES.SMS, overrideParams, false),
        };
      }

      if (normalizedTab === ACTIVITY_TYPES.NOTES) {
        return {
          ...notesCollection,
          items: noteItems,
          refresh: () => handleFetch(ACTIVITY_TYPES.NOTES, {}, true),
          fetch: (overrideParams) =>
            handleFetch(ACTIVITY_TYPES.NOTES, overrideParams, false),
        };
      }

      return {
        ...DEFAULT_COLLECTION_STATE,
        refresh: () => Promise.resolve(null),
        fetch: () => Promise.resolve(null),
      };
    },
    [
      allCollection,
      emailCollection,
      emailItems,
      handleFetch,
      notesCollection,
      noteItems,
      smsCollection,
      smsItems,
    ]
  );

  const handleCreateNote = useCallback(
    async (noteData) => {
      try {
        const response = await createNoteActivity(noteData);

        await handleFetch(ACTIVITY_TYPES.NOTES, {}, true);

        return response;
      } catch (error) {
        console.error("Error creating note:", error);
        throw error;
      }
    },
    [createNoteActivity, handleFetch]
  );

  const handleGetNoteDetail = useCallback(
    async (noteUuid) => {
      try {
        const response = await getNoteDetailActivity(noteUuid);

        return {
          note_uuid: noteUuid,
          note_title: response.body?.name || "",
          note_content: response.body?.description || "",
          category: response.body?.categories?.[0]?.category_uuid || "",
          categories: response.body?.categories || [],
          tags: response.body?.tags || [],
          owner_uuid: response.body?.owner_uuid || null,
          owner_name: response.body?.owner_name || null,
          created_at: response.body?.created_at || null,
          updated_at: response.body?.updated_at || null,
          last_update: response.body?.updated_at || null,
          raw: response.body,
        };
      } catch (error) {
        console.error("Error getting note detail:", error);
        throw error;
      }
    },
    [getNoteDetailActivity]
  );

  const handleUpdateNote = useCallback(
    async (noteData) => {
      try {
        const response = await updateNoteActivity(noteData);

        await handleFetch(ACTIVITY_TYPES.NOTES, {}, true);

        return response;
      } catch (error) {
        console.error("Error updating note:", error);
        throw error;
      }
    },
    [updateNoteActivity, handleFetch]
  );

  const handleDeleteNote = useCallback(
    async (noteUuid) => {
      removeNoteOptimistically(noteUuid);

      try {
        const response = await deleteNoteActivity(noteUuid);

        await Promise.all([
          handleFetch(ACTIVITY_TYPES.NOTES, {}, true),
          handleFetch(ACTIVITY_TYPES.ALL, {}, true),
        ]);

        return response;
      } catch (error) {
        console.error("Error deleting note:", error);
        await Promise.all([
          handleFetch(ACTIVITY_TYPES.NOTES, {}, true),
          handleFetch(ACTIVITY_TYPES.ALL, {}, true),
        ]);
        throw error;
      }
    },
    [deleteNoteActivity, handleFetch, removeNoteOptimistically]
  );

  return {
    contactId,
    activeTab,
    currentCollection,
    handleTabChange,
    handlePageChange,
    handleItemsPerPageChange,
    handleRefresh,
    handleCreateNote,
    handleGetNoteDetail,
    handleUpdateNote,
    handleDeleteNote,
    getTabProps: buildTabProps,
    emailItems,
    noteItems,
    smsItems,
    allItems,
    isLoading: allCollection.loading,
    error: allCollection.error,
  };
};
